// ===== NEW/UPDATED FormikUploadFile.tsx =====

import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Alert
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Close as CloseIcon,
  GetApp as DownloadIcon
} from '@mui/icons-material';
import getEnvVars from 'src/config/env';
import { MAX_UPLOAD_FILE_SIZE_BYTES } from 'src/constants/uploadLimits';

export interface ExistingFile {
  name: string;
  url: string;
  type: 'existing';
  id: string;
}

interface FormikUploadFileProps {
  setFieldValue?: (field: string, value: any) => void;
  disabled?: boolean;
  name: string;
  label: string;
  placement?: string;
  type?: string;
  fieldValue?: any;
  listUI?: boolean;
  handleChange: (files: File[]) => void;
  multiSelect?: boolean;
  isErrorField?: boolean;
  existingFiles?: ExistingFile[];
}

export const FormikUploadFile: React.FC<FormikUploadFileProps> = ({
  setFieldValue,
  disabled = false,
  name,
  label,
  fieldValue,
  // listUI = false,
  handleChange,
  multiSelect = false,
  existingFiles = []
}) => {
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [previewDialog, setPreviewDialog] = useState<{
    open: boolean;
    file: File | ExistingFile | null;
  }>({
    open: false,
    file: null
  });

  const acceptedFileTypes = '.jpg,.jpeg,.png,.gif,.svg,.heic,.pdf';
  const maxFileSize = MAX_UPLOAD_FILE_SIZE_BYTES;

// ADD THIS HELPER FUNCTION TO GET FULL URL
const getFullUrl = (url: string): string => {
  // Handle null, undefined, or empty URLs
  if (!url || url === '') {
    return '#';
  }
  
  // If URL is already absolute (starts with http), return as-is
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  
  const { socketUrl } = getEnvVars();
  // If URL is relative, prepend the backend server URL
  const backendUrl = socketUrl || 'http://localhost:5000';
  
  // Ensure URL starts with / if it's relative
  const relativeUrl = url.startsWith('/') ? url : `/${url}`;
  return `${backendUrl}${relativeUrl}`;
};
  
  useEffect(() => {
    // Initialize with existing uploaded files if any
    if (fieldValue?.value && Array.isArray(fieldValue.value)) {
      setUploadedFiles(fieldValue.value);
    }
  }, [fieldValue]);

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    
    if (files.length === 0) return;

    // Validate file size and type
    const validFiles = files.filter(file => {
      if (file.size > maxFileSize) {
        alert(`File ${file.name} exceeds 10MB limit`);
        return false;
      }
      return true;
    });

    let newFiles: File[];
    if (multiSelect) {
      newFiles = [...uploadedFiles, ...validFiles];
    } else {
      newFiles = validFiles.slice(0, 1); // Only keep the first file if not multiSelect
    }

    setUploadedFiles(newFiles);
    setFieldValue?.(name, newFiles);
    handleChange(newFiles);
    
    // Clear the input
    event.target.value = '';
  };

  const handleRemoveFile = (index: number) => {
    const newFiles = uploadedFiles.filter((_, i) => i !== index);
    setUploadedFiles(newFiles);
    setFieldValue?.(name, newFiles);
    handleChange(newFiles);
  };

  const handleViewFile = (file: File | ExistingFile) => {
    setPreviewDialog({ open: true, file });
  };

  const handleClosePreview = () => {
    setPreviewDialog({ open: false, file: null });
  };

  const renderPreviewContent = () => {
    const { file } = previewDialog;
    if (!file) return null;

    if ('url' in file) {
      // Existing file
      const fileUrl = file.url || '';
      const fullUrl = getFullUrl(fileUrl);
      
      // Debug logging (remove in production if needed)
      // console.log('Document preview - Original URL:', fileUrl);
      // console.log('Document preview - Full URL:', fullUrl);
      // console.log('Environment:', process.env.REACT_APP_ENV);
      // console.log('Current hostname:', typeof window !== 'undefined' ? window.location.hostname : 'N/A');
      
      const fileExtension = fileUrl.split('.').pop()?.toLowerCase();
      if (['jpg', 'jpeg', 'png', 'gif', 'svg'].includes(fileExtension || '')) {
        return (
          <img 
            src={fullUrl} 
            alt={file.name}
            style={{ maxWidth: '100%', maxHeight: '80vh', objectFit: 'contain' }}
          />
        );
      } else if (fileExtension === 'pdf') {
        return (
          <Box textAlign="center" py={4}>
            <Typography variant="h6" mb={2}>PDF Preview</Typography>
            <iframe 
              src={fullUrl} 
              width="100%" 
              height="600px"
              style={{ border: 'none' }}
              title="PDF Preview"
            />
            <Box mt={2}>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={() => window.open(fullUrl, '_blank')}
              >
                Open in New Tab
              </Button>
            </Box>
          </Box>
        );
      }
    } else {
      // Newly uploaded file
      const fileType = file.type;
      if (fileType.startsWith('image/')) {
        const imageUrl = URL.createObjectURL(file);
        return (
          <img 
            src={imageUrl} 
            alt={file.name}
            style={{ maxWidth: '100%', maxHeight: '80vh', objectFit: 'contain' }}
            onLoad={() => URL.revokeObjectURL(imageUrl)}
          />
        );
      } else if (fileType === 'application/pdf') {
        const pdfUrl = URL.createObjectURL(file);
        return (
          <Box textAlign="center" py={4}>
            <Typography variant="h6" mb={2}>PDF Preview</Typography>
            <iframe 
              src={pdfUrl} 
              width="100%" 
              height="600px"
              style={{ border: 'none' }}
              title="PDF Preview"
            />
          </Box>
        );
      }
    }
    
    return (
      <Box textAlign="center" py={4}>
        <Typography>Preview not available for this file type</Typography>
      </Box>
    );
  };

  const hasFiles = uploadedFiles.length > 0 || existingFiles.length > 0;

  return (
    <Box>
      <Typography variant="subtitle2" mb={1}>
        {label}
      </Typography>
      
      {/* Upload Button */}
      <Button
        variant="outlined"
        component="label"
        startIcon={<CloudUploadIcon />}
        disabled={disabled}
        sx={{ mb: 2 }}
      >
        Upload Document
        <input
          type="file"
          hidden
          accept={acceptedFileTypes}
          multiple={multiSelect}
          onChange={handleFileUpload}
        />
      </Button>

      {/* File List */}
      {hasFiles && (
        <Box>
          {/* Existing Files */}
          {existingFiles.map((file) => (
            <Box key={file.id} mb={1}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  p: 1,
                  border: '1px solid #e0e0e0',
                  borderRadius: 1,
                  backgroundColor: '#f8f9fa'
                }}
              >
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {file.name}
                  </Typography>
                  <Chip 
                    label="Existing File" 
                    size="small" 
                    color="info" 
                    variant="outlined"
                    sx={{ mt: 0.5 }}
                  />
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <IconButton
                    size="small"
                    onClick={() => handleViewFile(file)}
                    color="primary"
                    title="View file"
                  >
                    <VisibilityIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => window.open(getFullUrl(file.url), '_blank')}
                    color="default"
                    title="Download file"
                  >
                    <DownloadIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            </Box>
          ))}

          {/* Newly Uploaded Files */}
          {uploadedFiles.map((file, index) => (
            <Box key={index} mb={1}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  p: 1,
                  border: '1px solid #e0e0e0',
                  borderRadius: 1,
                  backgroundColor: '#fff'
                }}
              >
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {file.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {(file.size / 1024 / 1024).toFixed(2)} MB
                  </Typography>
                  <Chip 
                    label="New Upload" 
                    size="small" 
                    color="success" 
                    variant="outlined"
                    sx={{ ml: 1 }}
                  />
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {/* <IconButton
                    size="small"
                    onClick={() => handleViewFile(file)}
                    color="primary"
                    title="View file"
                  >
                    <VisibilityIcon fontSize="small" />
                  </IconButton> */}
                  <IconButton
                    size="small"
                    onClick={() => handleRemoveFile(index)}
                    color="error"
                    title="Remove file"
                    disabled={disabled}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {/* File Format Info */}
      <Alert severity="info" sx={{ mt: 1 }}>
        <Typography variant="caption">
          Accepted formats: JPG, PNG, GIF, SVG, HEIC, PDF (Max: 10MB)
        </Typography>
      </Alert>

      {/* Preview Dialog */}
      <Dialog
        open={previewDialog.open}
        onClose={handleClosePreview}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            {previewDialog.file ? 
              ('url' in previewDialog.file ? previewDialog.file.name : previewDialog.file.name) 
              : 'File Preview'
            }
          </Typography>
          <IconButton onClick={handleClosePreview}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {renderPreviewContent()}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePreview}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
